import React, {forwardRef, ReactNode, useMemo} from "react";
import {createTheme, ThemeProvider} from "@mui/material/styles";
import {GlobalStyles} from "@mui/material";
import {Link as RouterLink, LinkProps as RouterLinkProps} from 'react-router';
import {LinkProps} from '@mui/material/Link';

const LinkBehavior = forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }
>((props, ref) => {
  const {href, ...other} = props;
  // Map href (Material UI) -> to (react-router)
  return <RouterLink ref={ref} to={href} {...other} />;
});
LinkBehavior.displayName = 'LinkBehavior';

let _theme = createTheme({
  palette: {
    primary: {
      main: '#143344',
    },
    secondary: {
      main: '#013743',
    },
    action: {
      hover: 'rgba(0, 0, 0, 0.05)',
    },
    background: {
      default: '#F0F3F7',
      paper: '#ffffff',
    },
  },
  typography: {
    h6: {
      fontSize: '0.9rem',
    },
    h5: {
      fontSize: '0.9rem',
      fontWeight: 'bold',
    },
    h4: {
      fontSize: '1.2rem',
      fontWeight: 'bold',
    },
    body2: {
      fontSize: '0.8rem',
    }
  },
});

_theme = createTheme(_theme, {
  palette: {
    content: _theme.palette.augmentColor({color: {main: '#2c3e50'}}),
    contentLight: _theme.palette.augmentColor({color: {main: '#eeeeee'}}),
    shade1: '#eeeeee',
    shade3: '#F0F3F7',
    shade2: '#d3dde4',
    primaryShade: '#fff7ea',
    contentShade: '#7f8c8d',
  },
});

// make all text instead of black #2c3e50
_theme = createTheme(_theme, {
  components: {
    MuiTypography: {
      defaultProps: {
        color: 'content',
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        color: 'primary',
        size: 'small',
      },
    },
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      } as LinkProps,
    },
    MuiButtonBase: {
      defaultProps: {
        LinkComponent: LinkBehavior,
      },
    },
  },
  typography: {
    allVariants: {
      color: '#2c3e50',
    },
  },
});

export const theme = _theme;

export default function Theme(props: { children: ReactNode }) {
  useMemo(() => {
    document.body.style.backgroundColor = theme.palette.background.default;
  }, []);
  return <ThemeProvider theme={theme}>
    <GlobalStyles styles={{
      '.ql-editor p': {
        whiteSpace: 'pre-wrap',
        wordWrap: 'break-word',
      },
    }} />
    {props.children}
  </ThemeProvider>;
}
